import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { isLoggedIn, getCachedUser, clearAuth, setCachedUser } from './lib/auth'
import { api } from './lib/api'
import type { User } from './lib/types'
import Layout from './components/Layout'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Trees from './pages/Trees'
import TreeView from './pages/TreeView'
import Calendar from './pages/Calendar'
import Circles from './pages/Circles'
import JoinPage from './pages/JoinPage'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import DeleteAccount from './pages/DeleteAccount'
import InviteLanding from './pages/InviteLanding'
import Support from './pages/Support'
import Profile from './pages/Profile'
import Admin from './pages/Admin'
import PendingApproval from './pages/PendingApproval'
import Guide from './pages/Guide'

export default function App() {
  const [user, setUser] = useState<User | null>(getCachedUser())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isLoggedIn()) {
      api.me().then(u => { setUser(u); setCachedUser(u) })
        .catch(() => { clearAuth(); setUser(null) })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const logout = () => { clearAuth(); setUser(null) }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-void-950">
        <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-gold-400" />
      </div>
    )
  }

  const needsApproval = user && !user.approved

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login onLogin={setUser} />} />
      <Route element={<Layout user={user} onLogout={logout} />}>
        <Route path="/dashboard" element={
          !user ? <Navigate to="/login" /> : needsApproval ? <PendingApproval /> : <Dashboard user={user} onUpdate={u => { setUser(u); setCachedUser(u) }} />
        } />
        <Route path="/trees" element={
          !user ? <Navigate to="/login" /> : needsApproval ? <PendingApproval /> : <Trees />
        } />
        <Route path="/trees/:id" element={
          !user ? <Navigate to="/login" /> : needsApproval ? <PendingApproval /> : <TreeView />
        } />
        <Route path="/calendar" element={
          !user ? <Navigate to="/login" /> : needsApproval ? <PendingApproval /> : <Calendar />
        } />
        <Route path="/circles" element={
          !user ? <Navigate to="/login" /> : needsApproval ? <PendingApproval /> : <Circles user={user} />
        } />
        <Route path="/join/:token" element={
          needsApproval ? <PendingApproval /> : <JoinPage />
        } />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/i/:code" element={<InviteLanding />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/support" element={
          !user ? <Navigate to="/login" /> : needsApproval ? <PendingApproval /> : <Support />
        } />
        <Route path="/profile" element={user ? <Profile user={user} onUpdate={setUser} /> : <Navigate to="/login" />} />
        <Route path="/admin" element={user?.role === 'admin' ? <Admin /> : <Navigate to="/dashboard" />} />
        <Route path="/pending" element={<PendingApproval />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  )
}
